import * as React from "react";
import { cn } from "../lib/cn";

/**
 * AnimatedSeparator — a horizontal divider whose reveal grows CENTER-OUT and
 * whose exit collapses center-inward (PQA#2642; ported from the unified
 * approve-card one-off per the ui-first directive, so unified and v3 share
 * one motion source).
 *
 * Transition-only on purpose: it mounts at `scale-x-0` and flips to
 * `scale-x-100` on the next animation frame, so the grow is a plain CSS
 * transition and consumers ship NO keyframes, preset changes, or animation
 * wiring. `collapsed` drives the reverse beat (center-inward) with the same
 * transition. Motion-reduce drops the transition entirely: the line simply
 * is there, or is not.
 *
 *   <AnimatedSeparator />                    // reveals center-out on mount
 *   <AnimatedSeparator collapsed={done} />   // collapses center-in when done
 *
 * Role wiring mirrors `Separator` (decorative by default, `role="none"`);
 * horizontal-only, because the center-out grow is a horizontal idea — a
 * vertical variant earns its way in when a surface actually wants one.
 */
export interface AnimatedSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  /** True collapses the line center-inward (the exit beat). Default false. */
  collapsed?: boolean;
  /** When true (default), renders `role="none"` so screen readers ignore it. */
  decorative?: boolean;
}

export const AnimatedSeparator = React.forwardRef<HTMLDivElement, AnimatedSeparatorProps>(
  ({ collapsed = false, decorative = true, className, ...props }, ref) => {
    const localRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };
    // The browser must establish the scale-x-0 computed style BEFORE the flip
    // or the reveal jumps instead of transitioning. A single rAF is not enough:
    // on interaction-caused mounts React 18 can flush mount effects pre-paint,
    // so the rAF fires inside the same rendering frame (ui#232 review). The
    // forced-reflow read in a LAYOUT effect is the strongest guarantee - it
    // computes the before-change style synchronously, and the state flip in
    // the same commit then transitions from it (the classic FLIP read).
    const [revealed, setRevealed] = React.useState(false);
    React.useLayoutEffect(() => {
      localRef.current?.getBoundingClientRect();
      setRevealed(true);
    }, []);

    return (
      <div
        ref={setRefs}
        role={decorative ? "none" : "separator"}
        aria-orientation={!decorative ? "horizontal" : undefined}
        className={cn(
          "h-[1px] w-full shrink-0 origin-center bg-border/70 transition-transform duration-[360ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
          revealed && !collapsed ? "scale-x-100" : "scale-x-0",
          className,
        )}
        {...props}
      />
    );
  },
);
AnimatedSeparator.displayName = "AnimatedSeparator";
